/** sessionStorage key for soft-unauthorized → hard-logout escalation. */
export const SOFT_UNAUTHORIZED_CIRCUIT_KEY = 'apexx.hub.softUnauthorizedCircuit';

/** Soft unauthorized attempts within this window count toward escalation. */
export const SOFT_UNAUTHORIZED_WINDOW_MS = 5 * 60 * 1000;

/** Escalate to full SSO logout on this many soft unauthorized teardowns in the window. */
export const SOFT_UNAUTHORIZED_ESCALATE_AFTER = 2;

export type SoftUnauthorizedCircuitState = {
	count: number;
	firstAt: number;
	lastAt: number;
};

export type SoftUnauthorizedCircuitDecision = {
	/** When true, the next teardown should end the Keycloak SSO session. */
	escalate: boolean;
	/** Soft unauthorized count in the current window (after recording this attempt). */
	count: number;
};

export type SoftUnauthorizedCircuitOptions = {
	windowMs?: number;
	escalateAfter?: number;
	now?: number;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function readState(storage: StorageLike): SoftUnauthorizedCircuitState | null {
	try {
		const raw = storage.getItem(SOFT_UNAUTHORIZED_CIRCUIT_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as SoftUnauthorizedCircuitState;
		if (
			typeof parsed?.count !== 'number'
			|| typeof parsed?.firstAt !== 'number'
			|| typeof parsed?.lastAt !== 'number'
		) {
			return null;
		}

		return parsed;
	} catch {
		return null;
	}
}

function writeState(storage: StorageLike, state: SoftUnauthorizedCircuitState): void {
	storage.setItem(SOFT_UNAUTHORIZED_CIRCUIT_KEY, JSON.stringify(state));
}

/** Clears the soft-unauthorized escalation marker (e.g. after full SSO logout). */
export function clearSoftUnauthorizedCircuit(storage: StorageLike): void {
	storage.removeItem(SOFT_UNAUTHORIZED_CIRCUIT_KEY);
}

/**
 * Records a soft unauthorized teardown and returns whether to escalate to full SSO logout.
 * Escalates when {@link SOFT_UNAUTHORIZED_ESCALATE_AFTER} attempts occur within
 * {@link SOFT_UNAUTHORIZED_WINDOW_MS} (defaults: 2 in 5 minutes).
 */
export function recordSoftUnauthorizedAttempt(
	storage: StorageLike,
	options: SoftUnauthorizedCircuitOptions = {},
): SoftUnauthorizedCircuitDecision {
	const now = options.now ?? Date.now();
	const windowMs = options.windowMs ?? SOFT_UNAUTHORIZED_WINDOW_MS;
	const escalateAfter = options.escalateAfter ?? SOFT_UNAUTHORIZED_ESCALATE_AFTER;

	const previous = readState(storage);
	const inWindow = previous !== null && now - previous.firstAt <= windowMs;
	const count = inWindow ? previous.count + 1 : 1;
	const firstAt = inWindow ? previous.firstAt : now;

	writeState(storage, { count, firstAt, lastAt: now });

	return {
		escalate: count >= escalateAfter,
		count,
	};
}
